<div class="debtor-name-header flex vcenter">
  <h1 class="debtor-name-label">
    {{ "CASE_FORM.FORMS.INVOICE.DEBTOR_NAME_LABEL" | translate }}
  </h1>
</div>

@if (collapsed() && debtor) {
  <div
    class="debtor-collapsed-view flex column"
    data-testid="debtor-collapsed"
  >
    <section
      class="debtor-summary-card flex vcenter"
      role="button"
      tabindex="0"
      data-testid="debtor-summary"
      (click)="expandForm()"
      (keydown.enter)="expandForm()"
      (keydown.space)="expandForm(); $event.preventDefault()"
    >
      <payee-icon
        class="checkmark"
        name="checkmark-green-circle"
      ></payee-icon>
      <div class="summary-info grow1 flex column">
        <span
          class="summary-name"
          data-testid="debtor-summary-name"
        >
          {{ debtor.name }}
        </span>
        <span
          class="summary-meta flex vcenter"
          data-testid="debtor-summary-meta"
        >
          @if (debtor.tax_number) {
            <span data-testid="debtor-summary-tax-number">{{ debtor.tax_number }}</span>
          }
          @if (debtor.tax_number && addressSummary) {
            <span class="summary-dot">·</span>
          }
          @if (addressSummary) {
            <span>{{ addressSummary }}</span>
          }
        </span>
      </div>
      @if (debtorForm?.enabled) {
        <payee-icon
          name="pencil"
          width="18px"
          height="18px"
        ></payee-icon>
      }
    </section>
  </div>
}

@if (!collapsed()) {
  <section class="debtor-name-wrapper flex column">
    <app-auto-fill-section orPosition="bottom"></app-auto-fill-section>
    <div class="jurisdiction-field flex column">
      <span class="field-label">
        {{ "CASE_FORM.FORMS.DEBTOR.JURISDICTION_LABEL" | translate }}
      </span>
      <payee-dropdown
        width="max"
        height="tall"
        [formControl]="jurisdiction"
        [options]="jurisdictionOptions"
        [displayWith]="countryDisplayWith"
        [filterable]="true"
      >
        <ng-template
          #menuDisplayTemplate
          let-selected
        >
          <div class="flex vcenter country-with-flag">
            <span class="country-flag">{{ selected | countryFlag }}</span>
            <span>{{ countryDisplayWith(selected) }}</span>
          </div>
        </ng-template>
        <ng-template
          #itemDisplayTemplate
          let-option
        >
          <div class="flex vcenter country-with-flag">
            <span class="country-flag">{{ option | countryFlag }}</span>
            <span>{{ countryDisplayWith(option) }}</span>
          </div>
        </ng-template>
      </payee-dropdown>
    </div>
    <div
      class="debtor-type-selector flex"
      data-testid="debtor-type-selector"
    >
      <button
        class="type-option"
        data-testid="debtor-type-org"
        [class.active]="debtor?.debtor_type === 'org'"
        (click)="switchDebtorType('org')"
      >
        <mat-icon>business</mat-icon>
        <span class="type-label">
          {{ "CASE_FORM.FORMS.INVOICE.TYPE_ORG" | translate }}
        </span>
      </button>
      <button
        class="type-option"
        data-testid="debtor-type-ind"
        [class.active]="debtor?.debtor_type === 'ind'"
        (click)="switchDebtorType('ind')"
      >
        <mat-icon>person</mat-icon>
        <span class="type-label">
          {{ "CASE_FORM.FORMS.INVOICE.TYPE_IND" | translate }}
        </span>
      </button>
    </div>
    @if (debtor?.debtor_type === "org") {
      <div class="field-group">
        <span class="field-label">
          {{ "CASE_FORM.FORMS.DEBTOR.IND.NAME_LABEL" | translate }}
        </span>
        <div
          #origin="matAutocompleteOrigin"
          class="debtor-name-input-container"
          matAutocompleteOrigin
          [class.autocomplete-open]="companyAuto.isOpen"
          [class.has-error]="name.invalid && name.dirty"
        >
          @if (companySearchEnabled) {
            <img
              alt=""
              src="assets/shared/global-images/icon-set/find-gray.svg"
              class="search-icon"
              aria-hidden="true"
            />
          }
          <input
            class="debtor-name-input"
            data-testid="debtor-name-input"
            [formControl]="customNameControl"
            [placeholder]="'CASE_FORM.FORMS.INVOICE.DEBTOR_NAME_PLACEHOLDER' | translate"
            [matAutocomplete]="companyAuto"
            [matAutocompleteConnectedTo]="origin"
            (input)="onCompanySearchInput($event)"
            (blur)="name.patchValue(customNameControl.value)"
          />
          @if (autocompleteSearchLoading) {
            <mat-progress-spinner
              mode="indeterminate"
              diameter="20"
            ></mat-progress-spinner>
          }
        </div>
        <mat-autocomplete
          #companyAuto="matAutocomplete"
          class="debtor-autocomplete-panel"
          [requireSelection]="false"
          [displayWith]="companyDisplayWith"
          (optionSelected)="companySelected($event.option.value)"
        >
          <div class="options-scroll-area">
            @for (option of companySearchOptions; track option.tax_number || option.name) {
              <mat-option [value]="option">
                <div class="company-option">
                  <span class="company-option-name">{{ option.short_name || option.name }}</span>
                  <span class="company-option-meta">
                    @if (option.tax_number) {
                      <span class="company-option-tax">
                        {{ formatTaxNumber(option.tax_number) }}
                      </span>
                    }
                    @if (formatCompanyAddress(option); as addr) {
                      <span class="company-option-address">
                        {{ addr }}
                      </span>
                    }
                  </span>
                </div>
              </mat-option>
            }
          </div>
        </mat-autocomplete>
        @if (name.invalid && name.dirty) {
          <div class="debtor-name-error">
            @if (name.hasError("required")) {
              <mat-error>
                {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
              </mat-error>
            }
          </div>
        }
        @if (name.valid && name.value) {
          <span class="field-label">
            {{ "CASE_FORM.FORMS.INVOICE.TAX_NUMBER_LABEL" | translate }}
          </span>
          <payee-form-field
            width="max"
            height="tall"
          >
            <input
              payee-input
              [formControl]="tax_number"
              [placeholder]="'CASE_FORM.FORMS.INVOICE.TAX_NUMBER_PLACEHOLDER' | translate"
            />
          </payee-form-field>
          <button
            type="button"
            payee-button
            color="blue-payee"
            width="max"
            class="save-debtor-btn"
            data-testid="debtor-save"
            [disabled]="!debtorForm?.valid || !tax_number.value || companyUpdateLoading()"
            (click)="collapseIfValid()"
          >
            {{ "CASE_FORM.FORMS.INVOICE.SAVE" | translate }}
          </button>
          @if (showSaveRequiredError) {
            <div class="save-required-error">
              <span color="alert-red">
                {{ "CASE_FORM.FORMS.SAVE_REQUIRED" | translate }}
              </span>
            </div>
          }
        }
      </div>
    }
    @if (debtor?.debtor_type === "ind") {
      <div class="field-group">
        <span class="field-label">
          {{ "CASE_FORM.FORMS.DEBTOR.IND.NAME_LABEL" | translate }}
        </span>
        <payee-form-field
          width="max"
          height="tall"
        >
          <input
            payee-input
            data-testid="debtor-ind-name"
            [formControl]="name"
            [placeholder]="'CASE_FORM.FORMS.DEBTOR.IND.NAME_PLACEHOLDER' | translate"
          />
          @if (name.invalid && name.dirty) {
            <ng-container payee-error>
              @if (name.hasError("required")) {
                <span>
                  {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                </span>
              }
              @if (name.hasError("maxlength")) {
                <span>
                  {{ "ERROR_MESSAGES.MAXLENGTH" | translate }}
                </span>
              }
            </ng-container>
          }
        </payee-form-field>
        <span class="field-label">
          {{ "CASE_FORM.FORMS.DEBTOR.IND.CONTACT_LABEL" | translate }}
        </span>
        <payee-form-field
          width="max"
          height="tall"
        >
          <input
            payee-input
            data-testid="debtor-ind-postcode"
            [formControl]="postcode"
            [placeholder]="'CASE_FORM.FORMS.DEBTOR.IND.POSTCODE_PLACEHOLDER' | translate"
          />
          @if (postcode.invalid && postcode.dirty) {
            <ng-container payee-error>
              @if (postcode.hasError("required")) {
                <span>
                  {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                </span>
              }
            </ng-container>
          }
        </payee-form-field>
        <payee-form-field
          width="max"
          height="tall"
        >
          <input
            payee-input
            data-testid="debtor-ind-settlement"
            [formControl]="settlement"
            [placeholder]="'CASE_FORM.FORMS.DEBTOR.IND.SETTLEMENT_PLACEHOLDER' | translate"
          />
          @if (settlement.invalid && settlement.dirty) {
            <ng-container payee-error>
              @if (settlement.hasError("required")) {
                <span>
                  {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                </span>
              }
            </ng-container>
          }
        </payee-form-field>
        <payee-form-field
          width="max"
          height="tall"
        >
          <input
            payee-input
            data-testid="debtor-ind-street"
            [formControl]="street"
            [placeholder]="'CASE_FORM.FORMS.DEBTOR.IND.STREET_PLACEHOLDER' | translate"
          />
          @if (street.invalid && street.dirty) {
            <ng-container payee-error>
              @if (street.hasError("required")) {
                <span>
                  {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
                </span>
              }
            </ng-container>
          }
        </payee-form-field>
        <button
          type="button"
          payee-button
          color="blue-payee"
          width="max"
          class="save-debtor-btn"
          data-testid="debtor-save"
          [disabled]="!debtorForm?.valid || companyUpdateLoading()"
          (click)="collapseIfValid()"
        >
          {{ "CASE_FORM.FORMS.INVOICE.SAVE" | translate }}
        </button>
        @if (showSaveRequiredError) {
          <div class="save-required-error">
            <span color="alert-red">
              {{ "CASE_FORM.FORMS.SAVE_REQUIRED" | translate }}
            </span>
          </div>
        }
      </div>
    }
    @if (isGuest) {
      <div class="login-link">
        {{ "CASE_FORM.FORMS.INVOICE.ALREADY_REGISTERED" | translate }}
        <a routerLink="/login">{{ "CASE_FORM.FORMS.INVOICE.LOGIN" | translate }}</a>
      </div>
    }
  </section>
}
